import { useState, useEffect } from "react";
import { Music, TrendingUp, Star, ExternalLink, Send } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

export const MyTracksTab = ({ currentUser }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser?.email || currentUser?.user_id) {
      const params = new URLSearchParams();
      if (currentUser.email) params.append('email', currentUser.email);
      if (currentUser.user_id) params.append('user_id', currentUser.user_id);
      fetch(`${BACKEND_URL}/api/submissions/stats?${params.toString()}`)
        .then(r => r.json())
        .then(data => { setStats(data); setLoading(false); })
        .catch(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [currentUser?.email, currentUser?.user_id]);

  if (loading) return <div className="text-center text-white/40 py-12">Loading your tracks...</div>;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4" data-testid="my-tracks-summary">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center">
          <Music className="w-6 h-6 text-purple-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white" data-testid="my-tracks-count">{stats?.total_tracks || 0}</p>
          <p className="text-white/40 text-xs">Tracks Submitted</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center">
          <TrendingUp className="w-6 h-6 text-green-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-green-400" data-testid="my-tracks-plays">{stats?.total_plays || 0}</p>
          <p className="text-white/40 text-xs">Total Plays</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center">
          <Star className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-yellow-400" data-testid="my-tracks-featured">{stats?.tracks?.filter(t => t.featured).length || 0}</p>
          <p className="text-white/40 text-xs">Featured</p>
        </div>
      </div>

      {/* Track List */}
      {stats?.tracks?.length > 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="my-tracks-list">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <h2 className="text-lg font-bold text-white">Your Approved Tracks</h2>
            <a href="/submit" className="text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1 transition">
              <Send className="w-3.5 h-3.5" /> Submit Another
            </a>
          </div>
          <div className="divide-y divide-white/5">
            {stats.tracks.map((track, i) => (
              <div key={i} className="p-4 flex items-center justify-between hover:bg-white/[0.02] transition" data-testid={`my-track-${i}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Music className="w-5 h-5 text-purple-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium text-sm truncate">{track.track_title}</p>
                      {track.featured && (
                        <span className="text-[10px] bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full font-bold flex-shrink-0">FEATURED</span>
                      )}
                    </div>
                    <p className="text-white/40 text-xs">{track.genre || "No genre"} · {new Date(track.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                  <p className="text-white font-bold text-lg">{track.play_count || 0}</p>
                  <p className="text-white/40 text-[10px]">plays</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-10 text-center" data-testid="my-tracks-empty">
          <Music className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No Tracks Yet</h3>
          <p className="text-white/40 text-sm mb-6">Submit your music and get it in front of listeners across the DJsandMCMedia network.</p>
          <a href="/submit"
            className="inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-xl font-semibold transition"
            data-testid="submit-track-cta">
            <Send className="w-4 h-4" /> Submit Your Track
          </a>
        </div>
      )}

      {/* Link to full stats */}
      <div className="text-center">
        <a href="/artists" className="text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1 justify-center transition" data-testid="view-full-stats">
          <ExternalLink className="w-3.5 h-3.5" /> Browse All Artists
        </a>
      </div>
    </div>
  );
};
